import { useDataProvider } from "src/system/useDataProvider.ts";
import {
  type DefaultError,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
// todo: useDeleteOne hook should not depend on useResourceForm hook
import type { ValidSaveFormResponseData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import { useTranslation } from "react-i18next";
import { isUnauthorizedError } from "src/lib/axios/axios.utils.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";

export const useDeleteOne = <
  Data extends ValidSaveFormResponseData = ValidSaveFormResponseData,
>({
  resource,
  ...options
}: {
  resource: string;
} & UseMutationOptions<
  Data,
  DefaultError,
  {
    id: string;
    errorNotification?: {
      message: string;
      flash?: boolean;
    };
    successNotification?: {
      message: string;
      flash?: boolean;
    };
    disableErrorNotification?: boolean;
    disableSuccessNotification?: boolean;
  }
>) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const queries = useResourceQueries();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: [resource, "delete"],
    mutationFn: async ({ id }) => {
      return await dataProvider.deleteOne({
        resource: resource,
        id,
      });
    },
    ...options,
    onError: (error, variables, context) => {
      const { errorNotification, disableErrorNotification } = variables;
      if (!disableErrorNotification) {
        const defaultMessage = isUnauthorizedError<{
          message: string;
        }>(error)
          ? error.response?.data.message
          : // todo: translate
            "Couldn't delete the record";

        const { message = defaultMessage, flash = true } =
          errorNotification || {};

        snackbar.error({
          flash,
          message,
        });
      }
      options?.onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: queries.lists({
          resource,
        }),
      });
      queryClient.removeQueries({
        queryKey: queries.detail({
          id: variables.id,
          resource,
        }),
      });
      const { successNotification, disableSuccessNotification } = variables;
      if (!disableSuccessNotification) {
        const {
          message = t(`notifications.deleted`, {
            ns: getResourceI18nNamespace(resource),
          }),
          flash = true,
        } = successNotification || {};
        snackbar.success({
          flash,
          message,
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};
